import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, extractError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpRequired, setOtpRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(email, password, otpRequired ? otp : undefined);
      toast.success(`Welcome back, ${user.name}`);
      if (user.must_change_password) {
        navigate("/change-password-required", { replace: true });
      } else {
        navigate(user.role === "family" ? "/family" : user.role === "sitter" ? "/sitter" : user.role === "admin" ? "/admin" : "/");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 401 && typeof detail === "object" && detail?.code === "otp_required") {
        setOtpRequired(true);
        setError("Enter the 6-digit code from your authenticator app.");
      } else {
        const msg = extractError(err);
        setError(msg); toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-16">
        <span className="section-eyebrow">Welcome back</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">Log in to your account</h1>
        <p className="mt-2 text-stone2-600 text-sm">Continue building safe, specialized care for your family.</p>

        <form onSubmit={submit} className="mt-8 paper-card p-6 space-y-5" data-testid="login-form">
          <div>
            <label className="field-label" htmlFor="email">Email</label>
            <input id="email" data-testid={AUTH.loginEmail} type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="field-label" htmlFor="password">Password</label>
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-semibold text-sage-700 hover:text-sage-900">
                Forgot password?
              </Link>
            </div>
            <input id="password" data-testid={AUTH.loginPassword} type="password" required minLength={6}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none" />
          </div>
          {error && <div className="text-sm text-danger" data-testid="login-error">{error}</div>}
          {otpRequired && (
            <div>
              <label className="field-label" htmlFor="otp">6-digit code</label>
              <input id="otp" data-testid="login-otp-input" required
                inputMode="numeric" maxLength={6}
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-center font-mono text-lg tracking-widest focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none" />
            </div>
          )}
          <button type="submit" data-testid={AUTH.loginSubmit} disabled={loading}
            className="btn-primary w-full justify-center">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Log in
          </button>
        </form>

        <p className="mt-6 text-sm text-stone2-600">
          Don't have an account? <Link to="/register" className="text-sage-800 font-semibold hover:underline">Create one</Link>
        </p>
      </div>
    </div>
  );
}
